import { useEffect, useState } from "react";
import api, { rupiah, errMsg } from "../lib/api";
import { toast } from "sonner";
import { FileDown, CalendarRange, ChevronLeft, ChevronRight, Lock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const thisMonth = () => new Date().toISOString().slice(0, 7);
const shiftMonth = (m, delta) => {
  const [y, mo] = m.split("-").map(Number);
  const d = new Date(y, mo - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

export default function MonthlyReport() {
  const [month, setMonth] = useState(thisMonth());
  const [data, setData] = useState(null);

  const load = () => api.get(`/reports/monthly-sales?month=${month}`).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, [month]);

  const download = async (fmt) => {
    try {
      const res = await fetch(`${API}/reports/export?kind=monthly&month=${month}&fmt=${fmt}`, {
        credentials: "include", headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      if (!res.ok) throw new Error("Gagal mengunduh");
      const blob = await res.blob(); const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = `laporan_bulanan_${month}.${fmt === "pdf" ? "pdf" : "xlsx"}`; a.click();
      URL.revokeObjectURL(url); toast.success("Laporan diunduh");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const s = data?.summary;
  return (
    <div className="space-y-5 animate-fadeup report-legacy-font">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl sm:text-3xl font-display font-extrabold">Laporan Bulanan</h1>
          <p className="text-[#52525B] text-sm">Rekap penjualan, modal & profit (akses admin)</p>
        </div>
        <div className="flex items-center gap-2">
          <CalendarRange className="w-5 h-5" />
          <button data-testid="monthly-prev" onClick={() => setMonth((m) => shiftMonth(m, -1))} className="nb-btn px-2.5 py-2 bg-white" title="Bulan lalu"><ChevronLeft className="w-4 h-4" /></button>
          <input data-testid="monthly-month" type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="nb-input w-auto" />
          <button data-testid="monthly-next" onClick={() => setMonth((m) => shiftMonth(m, 1))} className="nb-btn px-2.5 py-2 bg-white" title="Bulan depan"><ChevronRight className="w-4 h-4" /></button>
        </div>
      </div>

      {s && (
        <div className="grid grid-cols-2 lg:grid-cols-7 gap-4 items-start">
          <div className="nb-card p-4"><span className="label-tag">Harga Kotor</span><div className="font-display font-extrabold text-xl mt-1">{rupiah(s.harga_kotor)}</div></div>
          <div className="nb-card p-4"><span className="label-tag">Extra Bubble</span><div className="font-display font-extrabold text-xl mt-1">{rupiah(s.extra_bubble)}</div></div>
          <div className="nb-card p-4"><span className="label-tag">Harga Modal</span><div className="font-display font-extrabold text-xl mt-1">{rupiah(s.harga_modal)}</div></div>
          <div className="nb-card p-4" style={{ background: "var(--keluar)" }}><span className="label-tag">Pot Marketplace</span><div className="font-display font-extrabold text-xl mt-1">{rupiah(s.pot_mp)}</div></div>
          <div className="nb-card p-4" style={{ background: "var(--keluar)" }}><span className="label-tag">Disc / Voucher</span><div className="font-display font-extrabold text-xl mt-1">{rupiah(s.disc)}</div></div>
          <div className="nb-card p-4" style={{ background: "var(--keluar)" }}><span className="label-tag">Pajak</span><div className="font-display font-extrabold text-xl mt-1">{rupiah(s.pajak || 0)}</div></div>
          <div className="nb-card p-4" style={{ background: "var(--masuk)" }}><span className="label-tag">Profit (Akhir − HPP)</span><div className="font-display font-extrabold text-xl mt-1">{rupiah(s.harga_akhir)}</div></div>
        </div>
      )}

      {s && (
        <div className="nb-card p-4" data-testid="stat-nilai-stok-modal" style={{ background: "var(--primary)" }}>
          <span className="label-tag">Nilai Stok (Modal) Saat Ini</span>
          <div className="font-display font-extrabold text-2xl mt-1">{rupiah(s.nilai_stok_modal)}</div>
          <p className="text-xs text-[#52525B] mt-1">Total HPP × stok yang tersedia sekarang</p>
        </div>
      )}

      <div className="flex gap-2">
        <button data-testid="monthly-export-excel" onClick={() => download("excel")} className="nb-btn px-4 py-2.5 flex items-center gap-2 bg-white"><FileDown className="w-4 h-4" /> Excel</button>
        <button data-testid="monthly-export-pdf" onClick={() => download("pdf")} className="nb-btn px-4 py-2.5 flex items-center gap-2 text-[#0A0A0A]" style={{ background: "var(--primary)" }}><FileDown className="w-4 h-4" /> PDF</button>
      </div>

      <div className="nb-card p-0 overflow-x-auto" data-testid="monthly-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b-2 border-[#0A0A0A]" style={{ background: "var(--primary)" }}>
              {["Tgl", "Tipe", "Warna", "Barang", "Qty", "Harga Kotor", "Extra Bubble", "HPP", "Pot MP", "Disc/Voucher", "Pajak", "Profit"].map((h) => (
                <th key={h} className="text-left font-bold px-3 py-2 whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(!data || data.rows.length === 0) ? (
              <tr><td colSpan={12} className="text-center text-[#52525B] py-8" data-testid="monthly-empty">Belum ada penjualan untuk bulan ini. Data bulan lain tidak hilang — klik <b>◀ Bulan lalu</b> atau pilih bulan di atas untuk melihatnya.</td></tr>
            ) : data.rows.map((r, idx) => (
              <tr key={idx} className="border-b border-[#0A0A0A]/20">
                <td className="px-3 py-2 whitespace-nowrap">{r.date}</td>
                <td className="px-3 py-2">{r.tipe}</td>
                <td className="px-3 py-2">{r.warna}</td>
                <td className="px-3 py-2">{r.item_name}</td>
                <td className="px-3 py-2">{r.qty}</td>
                <td className="px-3 py-2 whitespace-nowrap">{rupiah(r.harga_kotor)}</td>
                <td className="px-3 py-2 whitespace-nowrap">{rupiah(r.extra_bubble || 0)}</td>
                <td className="px-3 py-2 whitespace-nowrap">
                  {r.locked ? (
                    <span data-testid={`hpp-locked-${idx}`} className="inline-flex items-center gap-1" title="HPP dikunci — tidak berubah walau HPP master diedit" style={{ color: "var(--masuk)" }}>
                      <Lock className="w-3.5 h-3.5" strokeWidth={2.5} /> {rupiah(r.harga_modal)}
                    </span>
                  ) : rupiah(r.harga_modal)}
                </td>
                <td className="px-3 py-2 whitespace-nowrap">{rupiah(r.pot_mp || 0)}</td>
                <td className="px-3 py-2 whitespace-nowrap">{rupiah(r.disc || 0)}</td>
                <td className="px-3 py-2 whitespace-nowrap">{rupiah(r.pajak || 0)}</td>
                <td className="px-3 py-2 whitespace-nowrap font-bold" style={{ color: r.harga_akhir >= 0 ? "var(--masuk)" : "var(--retur)" }}>{rupiah(r.harga_akhir)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
